import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FileText, FilePlus, Mail, Eye, PlusCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminDashboard() {
  const [stats, setStats] = useState({
    posts: 0,
    pages: 0,
    messages: 0
  });
  const [recentPosts, setRecentPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [postsRes, pagesRes, messagesRes] = await Promise.all([
        axios.get(`${API}/posts?published_only=false`, { withCredentials: true }),
        axios.get(`${API}/pages`, { withCredentials: true }),
        axios.get(`${API}/contact/messages`, { withCredentials: true })
      ]);

      setStats({
        posts: postsRes.data.length,
        pages: pagesRes.data.length,
        messages: messagesRes.data.filter(m => !m.read).length
      });

      setRecentPosts(postsRes.data.slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: 'Total Posts', value: stats.posts, icon: FileText, color: 'bg-blue-500/10 text-blue-500' },
    { label: 'Total Pages', value: stats.pages, icon: FilePlus, color: 'bg-green-500/10 text-green-500' },
    { label: 'Unread Messages', value: stats.messages, icon: Mail, color: 'bg-amber-500/10 text-amber-500' }
  ];

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-8">
          <div className="h-8 bg-muted rounded w-1/3" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-muted rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground">Welcome back! Here's an overview of your site.</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <div 
              key={stat.label}
              className="p-6 border border-border rounded-lg bg-card"
              data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                  <p className="text-3xl font-bold mt-1">{stat.value}</p>
                </div>
                <div className={`p-3 rounded-lg ${stat.color}`}>
                  <Icon className="h-6 w-6" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
        <div className="flex flex-wrap gap-3">
          <Link to="/admin/posts/new">
            <Button data-testid="quick-new-post">
              <PlusCircle className="mr-2 h-4 w-4" />
              New Post
            </Button>
          </Link>
          <Link to="/admin/pages/new">
            <Button variant="outline" data-testid="quick-new-page">
              <PlusCircle className="mr-2 h-4 w-4" />
              New Page
            </Button>
          </Link>
          <Link to="/" target="_blank">
            <Button variant="outline" data-testid="quick-view-site">
              <Eye className="mr-2 h-4 w-4" />
              View Site
            </Button>
          </Link>
        </div>
      </div>

      {/* Recent Posts */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Recent Posts</h2>
          <Link to="/admin/posts" className="text-sm text-primary hover:underline">
            View all
          </Link>
        </div>
        
        {recentPosts.length > 0 ? (
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">Title</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {recentPosts.map((post) => (
                  <tr key={post.id} className="border-t border-border">
                    <td className="px-4 py-3">
                      <Link 
                        to={`/admin/posts/${post.slug}`}
                        className="font-medium hover:text-primary transition-colors"
                      >
                        {post.title}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                        post.published 
                          ? 'bg-green-500/10 text-green-500' 
                          : 'bg-amber-500/10 text-amber-500'
                      }`}>
                        {post.published ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {new Date(post.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 border border-dashed border-border rounded-lg">
            <p className="text-muted-foreground mb-4">No posts yet</p>
            <Link to="/admin/posts/new">
              <Button size="sm">Create your first post</Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
